/**
 * CRMSettings.jsx
 * Super Admin — CRM > Settings
 *
 * Features:
 *  - Settings summary header (active providers, template counts, total contacts)
 *  - Tab 1 — Provider Configuration:
 *      Accordion sections: SMS | WhatsApp | Voice | Meeting
 *      Configure modal per provider (credential fields shown as "***CONFIGURED***" when set)
 *      "Set as Active" per provider type
 *      PUT /api/crm/settings/providers/:type/:name
 *  - Tab 2 — Message Templates:
 *      Sub-tabs: SMS | WhatsApp | Voice
 *      List with edit / delete
 *      New / Edit modal with auto-detected variables ({{...}} pattern)
 *      POST / PUT / DELETE /api/crm/settings/templates[/:id]
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Settings, MessageSquare, MessageCircle, Phone, Video,
  ChevronDown, ChevronRight, Plus, Edit2, Trash2, X,
  Check, AlertCircle, CheckCircle, RefreshCw, Shield,
  Eye, EyeOff, Save,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHeaders = () => ({ Authorization: 'Bearer ' + localStorage.getItem('session_token') });

// ── Provider definitions ──────────────────────────────────────────────────────

const PROVIDER_DEFS = {
  sms: {
    label: 'SMS Providers',
    icon: MessageSquare,
    iconColor: 'text-yellow-600',
    iconBg: 'bg-yellow-50',
    iconBorder: 'border-yellow-100',
    providers: [
      {
        name: 'msg91',
        label: 'MSG91',
        fields: [
          { key: 'auth_key',          label: 'Auth Key',          type: 'password' },
          { key: 'sender_id',         label: 'Sender ID',         type: 'text' },
          { key: 'dlt_template_id',   label: 'DLT Template ID',   type: 'text' },
        ],
      },
      {
        name: 'twilio',
        label: 'Twilio',
        fields: [
          { key: 'account_sid',  label: 'Account SID',  type: 'text' },
          { key: 'auth_token',   label: 'Auth Token',   type: 'password' },
          { key: 'from_number',  label: 'From Number',  type: 'text' },
        ],
      },
    ],
  },
  whatsapp: {
    label: 'WhatsApp Providers',
    icon: MessageCircle,
    iconColor: 'text-green-600',
    iconBg: 'bg-green-50',
    iconBorder: 'border-green-100',
    providers: [
      {
        name: 'interakt',
        label: 'Interakt',
        fields: [
          { key: 'api_key',       label: 'API Key',       type: 'password' },
          { key: 'sender_number', label: 'Sender Number', type: 'text' },
        ],
      },
      {
        name: 'wati',
        label: 'WATI',
        fields: [
          { key: 'api_endpoint',   label: 'API Endpoint',   type: 'text' },
          { key: 'access_token',   label: 'Access Token',   type: 'password' },
        ],
      },
    ],
  },
  voice: {
    label: 'Voice Providers',
    icon: Phone,
    iconColor: 'text-[#843FEF]',
    iconBg: 'bg-purple-50',
    iconBorder: 'border-purple-100',
    providers: [
      {
        name: 'twilio',
        label: 'Twilio',
        fields: [
          { key: 'account_sid',    label: 'Account SID',    type: 'text' },
          { key: 'auth_token',     label: 'Auth Token',     type: 'password' },
          { key: 'from_number',    label: 'From Number',    type: 'text' },
          { key: 'twiml_app_sid',  label: 'TwiML App SID',  type: 'text' },
        ],
      },
      {
        name: 'exotel',
        label: 'Exotel',
        fields: [
          { key: 'api_key',   label: 'API Key',   type: 'text' },
          { key: 'api_token', label: 'API Token', type: 'password' },
          { key: 'subdomain', label: 'Subdomain', type: 'text' },
          { key: 'caller_id', label: 'Caller ID', type: 'text' },
        ],
      },
    ],
  },
  meeting: {
    label: 'Meeting Providers',
    icon: Video,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-50',
    iconBorder: 'border-blue-100',
    providers: [
      {
        name: 'google_meet',
        label: 'Google Meet',
        fields: [
          { key: 'client_id',     label: 'Client ID',     type: 'text' },
          { key: 'client_secret', label: 'Client Secret', type: 'password' },
          { key: 'redirect_uri',  label: 'Redirect URI',  type: 'text' },
        ],
      },
      {
        name: 'zoom',
        label: 'Zoom',
        fields: [
          { key: 'api_key',    label: 'API Key',    type: 'text' },
          { key: 'api_secret', label: 'API Secret', type: 'password' },
        ],
      },
    ],
  },
};

const CONFIGURED_PLACEHOLDER = '***CONFIGURED***';

// ── Helpers ───────────────────────────────────────────────────────────────────

const extractVariables = (content) => {
  const matches = content.match(/\{\{([^}]+)\}\}/g) || [];
  return [...new Set(matches.map((m) => m.replace(/^\{\{|\}\}$/g, '').trim()))];
};

const fmt = (n) => (n !== null && n !== undefined ? Number(n).toLocaleString('en-IN') : '—');

// ── Sub-components ────────────────────────────────────────────────────────────

const StatusBadge = ({ active }) =>
  active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-semibold border border-green-200">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-semibold border border-gray-200">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      Inactive
    </span>
  );

// ── Modal ─────────────────────────────────────────────────────────────────────

const Modal = ({ isOpen, onClose, title, children, maxWidth = '480px' }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl w-full shadow-2xl overflow-hidden"
        style={{ maxWidth }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 max-h-[80vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// ── Provider Configure Modal ──────────────────────────────────────────────────

const ConfigureModal = ({ isOpen, onClose, providerType, providerName, providerDef, existingConfig, isActive, onSave, saving }) => {
  const [formValues, setFormValues]   = useState({});
  const [setAsActive, setSetAsActive] = useState(false);
  const [showField, setShowField]     = useState({});

  useEffect(() => {
    if (isOpen && providerDef) {
      const initial = {};
      providerDef.fields.forEach((f) => {
        initial[f.key] = existingConfig?.[f.key] ? CONFIGURED_PLACEHOLDER : '';
      });
      setFormValues(initial);
      setSetAsActive(isActive || false);
      setShowField({});
    }
  }, [isOpen, providerDef, existingConfig, isActive]);

  const handleChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    // Only send fields that are NOT the placeholder (i.e., actually changed by user)
    const payload = {};
    providerDef.fields.forEach((f) => {
      if (formValues[f.key] !== CONFIGURED_PLACEHOLDER && formValues[f.key] !== '') {
        payload[f.key] = formValues[f.key];
      }
    });
    onSave({ config: payload, set_as_active: setAsActive });
  };

  if (!providerDef) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Configure ${providerDef.label}`}>
      <div className="space-y-4">
        {providerDef.fields.map((field) => {
          const isConfigured = formValues[field.key] === CONFIGURED_PLACEHOLDER;
          const isPassword   = field.type === 'password';
          const visible      = showField[field.key];

          return (
            <div key={field.key}>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                {field.label}
              </label>
              <div className="relative">
                <input
                  type={isPassword && !visible ? 'password' : 'text'}
                  value={formValues[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={isConfigured ? CONFIGURED_PLACEHOLDER : `Enter ${field.label}`}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all pr-10"
                />
                {isPassword && (
                  <button
                    type="button"
                    onClick={() => setShowField((p) => ({ ...p, [field.key]: !p[field.key] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {visible ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                )}
              </div>
              {isConfigured && (
                <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                  <Shield size={10} /> Currently configured — leave blank to keep existing value
                </p>
              )}
            </div>
          );
        })}

        <div className="flex items-center gap-2 pt-2">
          <input
            id="set-active-chk"
            type="checkbox"
            checked={setAsActive}
            onChange={(e) => setSetAsActive(e.target.checked)}
            className="w-4 h-4 accent-[#843FEF] cursor-pointer"
          />
          <label htmlFor="set-active-chk" className="text-sm text-gray-700 font-medium cursor-pointer">
            Set as active {providerType} provider
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#843FEF] rounded-lg hover:bg-purple-700 disabled:opacity-60 transition-colors"
          >
            {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
            Save Configuration
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Template Modal ────────────────────────────────────────────────────────────

const TemplateModal = ({ isOpen, onClose, editing, defaultType, onSave, saving }) => {
  const [form, setForm] = useState({
    name: '', type: defaultType || 'sms', content: '', variables: [],
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (editing) {
        const editContent = editing.template_content || editing.content || '';
        setForm({
          name:      editing.template_name    || editing.name    || '',
          type:      editing.template_type    || editing.type    || defaultType || 'sms',
          content:   editContent,
          variables: editing.variables || extractVariables(editContent),
        });
      } else {
        setForm({ name: '', type: defaultType || 'sms', content: '', variables: [] });
      }
      setErrors({});
    }
  }, [isOpen, editing, defaultType]);

  const handleContentChange = (val) => {
    setForm((prev) => ({ ...prev, content: val, variables: extractVariables(val) }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Template name is required.';
    if (!form.content.trim()) e.content = 'Template content is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave(form);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editing ? 'Edit Template' : 'New Template'}
      maxWidth="540px"
    >
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Template Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Welcome Message"
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all ${
              errors.name ? 'border-red-400' : 'border-gray-200'
            }`}
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>

        {/* Type (read-only based on current sub-tab) */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Template Type</label>
          <div className="flex gap-2">
            {['sms', 'whatsapp', 'voice'].map((t) => (
              <button
                key={t}
                onClick={() => setForm((p) => ({ ...p, type: t }))}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg capitalize transition-colors ${
                  form.type === t
                    ? 'bg-[#843FEF] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Template Content *
            <span className="ml-1 text-gray-400 font-normal">
              (use {'{{variable_name}}'} for dynamic values)
            </span>
          </label>
          <textarea
            rows={5}
            value={form.content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder="Hi {{name}}, your demo is scheduled for {{date}} at {{time}}."
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all resize-none font-mono ${
              errors.content ? 'border-red-400' : 'border-gray-200'
            }`}
          />
          {errors.content && <p className="text-xs text-red-500 mt-1">{errors.content}</p>}
        </div>

        {/* Auto-detected variables */}
        {form.variables.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1.5">Detected Variables</p>
            <div className="flex flex-wrap gap-1.5">
              {form.variables.map((v) => (
                <span
                  key={v}
                  className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold bg-purple-50 text-[#843FEF] border border-purple-200 rounded-full"
                >
                  {`{{${v}}}`}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#843FEF] rounded-lg hover:bg-purple-700 disabled:opacity-60 transition-colors"
          >
            {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
            {editing ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Delete Confirm Modal ──────────────────────────────────────────────────────

const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, itemName, saving }) => (
  <Modal isOpen={isOpen} onClose={onClose} title="Confirm Delete" maxWidth="400px">
    <div className="text-center">
      <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-3">
        <Trash2 size={20} className="text-red-500" />
      </div>
      <p className="text-sm text-gray-700 mb-1">
        Are you sure you want to delete the template
      </p>
      <p className="text-sm font-bold text-gray-900 mb-5">"{itemName}"?</p>
      <p className="text-xs text-gray-400 mb-5">This action cannot be undone.</p>
      <div className="flex justify-center gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-60 transition-colors"
        >
          {saving ? <RefreshCw size={13} className="animate-spin" /> : <Trash2 size={13} />}
          Delete
        </button>
      </div>
    </div>
  </Modal>
);

// ── Toast ─────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, visible }) => {
  if (!visible) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-semibold transition-all ${
        isSuccess
          ? 'bg-green-600 text-white'
          : 'bg-red-600 text-white'
      }`}
    >
      {isSuccess ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
      {message}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const CRMSettings = () => {
  const [activeTab,     setActiveTab]     = useState(0); // 0=Providers, 1=Templates
  const [providers,     setProviders]     = useState({ sms: [], whatsapp: [], voice: [], meeting: [] });
  const [configModal,   setConfigModal]   = useState({ open: false, type: null, name: null, config: {} });
  const [templates,     setTemplates]     = useState([]);
  const [templateType,  setTemplateType]  = useState('sms');
  const [templateModal, setTemplateModal] = useState({ open: false, editing: null });
  const [deleteModal,   setDeleteModal]   = useState({ open: false, id: null, name: '' });
  const [summary,       setSummary]       = useState(null);
  const [accordionOpen, setAccordionOpen] = useState({ sms: true, whatsapp: false, voice: false, meeting: false });
  const [saving,        setSaving]        = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingSummary,   setLoadingSummary]   = useState(true);
  const [toast,  setToast]  = useState({ visible: false, message: '', type: 'success' });
  const [error,  setError]  = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((p) => ({ ...p, visible: false })), 3500);
  };

  // ── Fetch summary ────────────────────────────────────────────────────────

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/crm/settings/summary`, { headers: authHeaders() });
      const raw = res.data?.data || res.data || null;
      if (raw) {
        setSummary({
          active_sms_provider:      raw.active_providers?.sms      || null,
          active_whatsapp_provider: raw.active_providers?.whatsapp || null,
          active_voice_provider:    raw.active_providers?.voice    || null,
          sms_template_count:       raw.template_counts?.sms       || 0,
          whatsapp_template_count:  raw.template_counts?.whatsapp  || 0,
          total_contacts:           raw.total_contacts              || 0,
        });
      } else {
        setSummary(null);
      }
    } catch (err) {
      console.error('[CRMSettings] summary fetch error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load settings summary.');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  // ── Fetch providers ──────────────────────────────────────────────────────

  const fetchProviders = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/crm/settings/providers`, { headers: authHeaders() });
      const data = res.data?.data?.providers || res.data?.providers || res.data?.data || {};
      setProviders({
        sms:     data.sms      || [],
        whatsapp:data.whatsapp || [],
        voice:   data.voice    || [],
        meeting: data.meeting  || [],
      });
    } catch (err) {
      console.error('[CRMSettings] providers fetch error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load providers.');
    }
  }, []);

  // ── Fetch templates ──────────────────────────────────────────────────────

  const fetchTemplates = useCallback(async (type) => {
    setLoadingTemplates(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/crm/settings/templates?type=${type}`,
        { headers: authHeaders() }
      );
      const list = res.data?.data?.templates || res.data?.templates || res.data?.data || [];
      setTemplates(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[CRMSettings] templates fetch error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load templates.');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchProviders();
  }, [fetchSummary, fetchProviders]);

  useEffect(() => {
    if (activeTab === 1) fetchTemplates(templateType);
  }, [activeTab, templateType, fetchTemplates]);

  // ── Open configure modal ─────────────────────────────────────────────────

  const openConfigModal = (type, providerName) => {
    const typeDef = PROVIDER_DEFS[type];
    const pDef    = typeDef?.providers.find((p) => p.name === providerName);
    if (!pDef) return;

    // Find existing config for this provider from state
    const existing = providers[type]?.find((p) => p.name === providerName);
    const isActive = existing?.is_active || false;

    setConfigModal({
      open:     true,
      type,
      name:     providerName,
      config:   existing?.config || {},
      isActive,
      providerDef: pDef,
    });
  };

  const saveProviderConfig = async ({ config, set_as_active }) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/crm/settings/providers/${configModal.type}/${configModal.name}`,
        { config, is_active: set_as_active },
        { headers: authHeaders() }
      );
      showToast(`${configModal.name} configuration saved successfully.`, 'success');
      setConfigModal((p) => ({ ...p, open: false }));
      fetchProviders();
      fetchSummary();
    } catch (err) {
      console.error('[CRMSettings] save provider error:', err);
      showToast(err.response?.data?.message || 'Failed to save configuration.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Template CRUD ────────────────────────────────────────────────────────

  const saveTemplate = async (form) => {
    setSaving(true);
    const payload = {
      template_name:    form.name,
      template_type:    form.type,
      template_content: form.content,
      variables:        form.variables,
    };
    try {
      if (templateModal.editing) {
        await axios.put(
          `${API_BASE_URL}/api/crm/settings/templates/${templateModal.editing.id}`,
          payload,
          { headers: authHeaders() }
        );
        showToast('Template updated successfully.', 'success');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/crm/settings/templates`,
          payload,
          { headers: authHeaders() }
        );
        showToast('Template created successfully.', 'success');
      }
      setTemplateModal({ open: false, editing: null });
      fetchTemplates(templateType);
      fetchSummary();
    } catch (err) {
      console.error('[CRMSettings] save template error:', err);
      showToast(err.response?.data?.message || 'Failed to save template.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/api/crm/settings/templates/${deleteModal.id}`,
        { headers: authHeaders() }
      );
      showToast('Template deleted.', 'success');
      setDeleteModal({ open: false, id: null, name: '' });
      fetchTemplates(templateType);
      fetchSummary();
    } catch (err) {
      console.error('[CRMSettings] delete template error:', err);
      showToast(err.response?.data?.message || 'Failed to delete template.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Provider type -> active name lookup ──────────────────────────────────

  const getActiveProvider = (type) => {
    const found = providers[type]?.find((p) => p.is_active);
    return found?.label || found?.name || null;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-100 p-6">

      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center">
          <Settings size={18} className="text-[#843FEF]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">CRM Settings</h1>
          <p className="text-xs text-gray-400 mt-0.5">Provider configuration &amp; message templates</p>
        </div>
      </div>

      {/* ── Settings Summary ── */}
      {!loadingSummary && summary && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Settings Summary</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'SMS Provider',       value: getActiveProvider('sms')      || summary?.active_sms_provider      || '—' },
              { label: 'WhatsApp Provider',  value: getActiveProvider('whatsapp') || summary?.active_whatsapp_provider  || '—' },
              { label: 'Voice Provider',     value: getActiveProvider('voice')    || summary?.active_voice_provider     || '—' },
              { label: 'SMS Templates',      value: fmt(summary?.sms_template_count) },
              { label: 'WhatsApp Templates', value: fmt(summary?.whatsapp_template_count) },
              { label: 'Total Contacts',     value: fmt(summary?.total_contacts) },
            ].map((item) => (
              <div key={item.label} className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <p className="text-sm font-bold text-gray-900 truncate">{item.value}</p>
                <p className="text-[10px] text-gray-500 font-medium mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm w-fit mb-5">
        {['Provider Configuration', 'Message Templates'].map((tab, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
              activeTab === i
                ? 'bg-[#843FEF] text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          TAB 0: Provider Configuration
         ══════════════════════════════════════════════════════════════ */}
      {activeTab === 0 && (
        <div className="space-y-3">
          {Object.entries(PROVIDER_DEFS).map(([typeKey, typeDef]) => {
            const Icon   = typeDef.icon;
            const isOpen = accordionOpen[typeKey];
            const activeProviderName = getActiveProvider(typeKey);

            return (
              <div key={typeKey} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Accordion header */}
                <button
                  onClick={() => setAccordionOpen((p) => ({ ...p, [typeKey]: !p[typeKey] }))}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${typeDef.iconBg} ${typeDef.iconBorder} border flex items-center justify-center`}>
                      <Icon size={15} className={typeDef.iconColor} />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-800">{typeDef.label}</p>
                      {activeProviderName && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          Active: <span className="text-green-600 font-semibold">{activeProviderName}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  {isOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                </button>

                {/* Accordion body */}
                {isOpen && (
                  <div className="px-5 pb-5 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                      {typeDef.providers.map((pDef) => {
                        const providerState = providers[typeKey]?.find((p) => p.name === pDef.name);
                        const isActive      = providerState?.is_active || false;
                        const isConfigured  = providerState?.is_configured || false;

                        return (
                          <div
                            key={pDef.name}
                            className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                              isActive
                                ? 'border-purple-200 bg-purple-50'
                                : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold border ${
                                isActive
                                  ? 'bg-[#843FEF] text-white border-purple-400'
                                  : 'bg-white text-gray-600 border-gray-200'
                              }`}>
                                {pDef.label.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{pDef.label}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <StatusBadge active={isActive} />
                                  {isConfigured && (
                                    <span className="text-[10px] text-green-600 font-medium flex items-center gap-0.5">
                                      <Check size={10} /> Configured
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => openConfigModal(typeKey, pDef.name)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#843FEF] bg-white border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                            >
                              <Settings size={12} />
                              Configure
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 1: Message Templates
         ══════════════════════════════════════════════════════════════ */}
      {activeTab === 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

          {/* Sub-tab bar + New Template button */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
              {['sms', 'whatsapp', 'voice'].map((t) => (
                <button
                  key={t}
                  onClick={() => setTemplateType(t)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md capitalize transition-colors ${
                    templateType === t
                      ? 'bg-white text-[#843FEF] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t === 'sms' ? 'SMS' : t === 'whatsapp' ? 'WhatsApp' : 'Voice'}
                </button>
              ))}
            </div>
            <button
              onClick={() => setTemplateModal({ open: true, editing: null })}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#843FEF] rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
            >
              <Plus size={13} />
              New Template
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 mx-5 mt-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
              <AlertCircle size={15} />
              <span>{error}</span>
              <button
                onClick={() => fetchTemplates(templateType)}
                className="ml-auto text-xs font-semibold underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Template list */}
          <div className="p-5">
            {loadingTemplates ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <MessageSquare size={36} className="mb-3 opacity-30" />
                <p className="text-sm font-medium">No {templateType.toUpperCase()} templates yet.</p>
                <button
                  onClick={() => setTemplateModal({ open: true, editing: null })}
                  className="mt-3 text-xs font-semibold text-[#843FEF] hover:underline"
                >
                  + Create your first template
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.map((tpl) => {
                  const tplName    = tpl.template_name    || tpl.name    || '';
                  const tplContent = tpl.template_content || tpl.content || '';
                  const vars = tpl.variables || extractVariables(tplContent);
                  return (
                    <div
                      key={tpl.id}
                      className="flex items-start justify-between gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-purple-200 transition-colors group"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{tplName}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 font-mono">
                          {tplContent.slice(0, 100)}{tplContent.length > 100 ? '…' : ''}
                        </p>
                        {vars.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {vars.map((v) => (
                              <span
                                key={v}
                                className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-50 text-[#843FEF] border border-purple-200 rounded-full"
                              >
                                {`{{${v}}}`}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => setTemplateModal({ open: true, editing: tpl })}
                          className="p-1.5 text-gray-400 hover:text-[#843FEF] hover:bg-purple-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, id: tpl.id, name: tpl.template_name || tpl.name || '' })}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Configure Provider Modal ── */}
      <ConfigureModal
        isOpen={configModal.open}
        onClose={() => setConfigModal((p) => ({ ...p, open: false }))}
        providerType={configModal.type}
        providerName={configModal.name}
        providerDef={configModal.providerDef}
        existingConfig={configModal.config}
        isActive={configModal.isActive}
        onSave={saveProviderConfig}
        saving={saving}
      />

      {/* ── Template Modal ── */}
      <TemplateModal
        isOpen={templateModal.open}
        onClose={() => setTemplateModal({ open: false, editing: null })}
        editing={templateModal.editing}
        defaultType={templateType}
        onSave={saveTemplate}
        saving={saving}
      />

      {/* ── Delete Confirm Modal ── */}
      <DeleteConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null, name: '' })}
        onConfirm={deleteTemplate}
        itemName={deleteModal.name}
        saving={saving}
      />

      {/* ── Toast ── */}
      <Toast visible={toast.visible} message={toast.message} type={toast.type} />

    </div>
  );
};

export default CRMSettings;
